import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import MainLayout from './layouts/MainLayout';

// Pages
import LoginPage from './pages/LoginPage';
import SiteClosedPage from './pages/SiteClosedPage';
import InviteRegisterPage from './pages/InviteRegisterPage';
import DashboardPage from './pages/DashboardPage';
import ContractorsPage from './pages/ContractorsPage';
import JobsPage from './pages/JobsPage';
import ImportFromPOPage from './pages/ImportFromPOPage';
import ImportQuotePDFPage from './pages/ImportQuotePDFPage';
import QuotesPage from './pages/QuotesPage';
import PurchaseOrdersPage from './pages/PurchaseOrdersPage';
import PricingPage from './pages/PricingPage';
import InvoicesPage from './pages/InvoicesPage';
import InvoicingPage from './pages/InvoicingPage';
import SchedulePage from './pages/SchedulePage';
import EmailsPage from './pages/EmailsPage';
import ReportsPage from './pages/ReportsPage';
import SettingsPage from './pages/SettingsPage';
import ClientPortalPage from './pages/ClientPortalPage';
import ClientPortalDashboard from './pages/ClientPortalDashboard';
import TradePortalPage from './pages/TradePortalPage';
import DocumentsPage from './pages/DocumentsPage';
import JobDetailPage from './pages/JobDetailPage';

import './App.css';

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <MainLayout>{children}</MainLayout>;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Login page - now the default landing page */}
      <Route path="/" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      
      {/* Invite-only registration */}
      <Route path="/invite" element={<InviteRegisterPage />} />
      
      {/* Client Portal - Public quote view */}
      <Route path="/client/quote/:token" element={<ClientPortalPage />} />
      
      {/* Client Portal - Requires login */}
      <Route path="/client" element={<ClientPortalDashboard />} />
      <Route path="/client-portal" element={<Navigate to="/client" replace />} />
      
      {/* Trade Portal - Requires login */}
      <Route path="/trade" element={<TradePortalPage />} />
      <Route path="/trade-portal" element={<Navigate to="/trade" replace />} />

      {/* Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/contractors" element={<ProtectedRoute allowedRoles={['admin', 'staff', 'superuser']}><ContractorsPage /></ProtectedRoute>} />
      <Route path="/jobs" element={<ProtectedRoute><JobsPage /></ProtectedRoute>} />
      <Route path="/jobs/import-from-po" element={<ProtectedRoute allowedRoles={['admin', 'staff', 'superuser']}><ImportFromPOPage /></ProtectedRoute>} />
      <Route path="/quotes/import-pdf" element={<ProtectedRoute allowedRoles={['admin', 'staff', 'superuser']}><ImportQuotePDFPage /></ProtectedRoute>} />
      <Route path="/jobs/:jobId" element={<ProtectedRoute><JobDetailPage /></ProtectedRoute>} />
      <Route path="/quotes" element={<ProtectedRoute allowedRoles={['admin', 'staff', 'superuser']}><QuotesPage /></ProtectedRoute>} />
      <Route path="/purchase-orders" element={<ProtectedRoute allowedRoles={['admin', 'staff', 'superuser']}><PurchaseOrdersPage /></ProtectedRoute>} />
      <Route path="/pricing" element={<ProtectedRoute allowedRoles={['admin', 'staff', 'superuser']}><PricingPage /></ProtectedRoute>} />
      <Route path="/invoices" element={<ProtectedRoute allowedRoles={['admin', 'staff', 'superuser']}><InvoicesPage /></ProtectedRoute>} />
      <Route path="/invoicing" element={<ProtectedRoute allowedRoles={['admin', 'staff', 'superuser']}><InvoicingPage /></ProtectedRoute>} />
      <Route path="/schedule" element={<ProtectedRoute><SchedulePage /></ProtectedRoute>} />
      <Route path="/emails" element={<ProtectedRoute allowedRoles={['admin', 'staff', 'superuser']}><EmailsPage /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute allowedRoles={['admin', 'staff', 'superuser']}><ReportsPage /></ProtectedRoute>} />
      <Route path="/documents" element={<ProtectedRoute allowedRoles={['admin', 'staff', 'superuser']}><DocumentsPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute allowedRoles={['admin', 'superuser']}><SettingsPage /></ProtectedRoute>} />

      {/* Catch all - redirect to login */}
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
